/*
 * Copyright (C) 2022 もにょてっく. All Rights Reserved.
 *
 * @author もにょ〜ん <monyone.teihen@gmail.com>
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import NaluParser from './nalu-parser.ts';
import ExpGolomb from './exp-golomb.js';
import Log from '../utils/logger.js';

class H265NaluParser extends NaluParser {

    static parseVPS(uint8array) {
        let rbsp = H265NaluParser._ebsp2rbsp(uint8array);
        let gb = new ExpGolomb(rbsp);

        /* remove NALu Header */
        gb.readByte();
        gb.readByte();

        // VPS
        let video_parameter_set_id = gb.readBits(4);
        gb.readBits(2);
        let max_layers_minus1 = gb.readBits(6);
        let max_sub_layers_minus1 = gb.readBits(3);
        let temporal_id_nesting_flag = gb.readBool();
        // and more ...

        gb.destroy();
        gb = null;

        return {
            num_temporal_layers: max_sub_layers_minus1 + 1,
            temporal_id_nested: temporal_id_nesting_flag
        };
    }

    static parseSPS(uint8array) {
        let rbsp = H265NaluParser._ebsp2rbsp(uint8array);
        let gb = new ExpGolomb(rbsp);
        const sps = {};

        /* remove NALu Header */
        gb.readByte();
        gb.readByte();

        let left_offset = 0, right_offset = 0, top_offset = 0, bottom_offset = 0;

        // SPS
        let video_parameter_set_id = gb.readBits(4);
        let max_sub_layers_minus1 = gb.readBits(3);
        let temporal_id_nesting_flag = gb.readBool();

        // profile_tier_level begin
        let general_profile_space = gb.readBits(2);
        let general_tier_flag = gb.readBool();
        let general_profile_idc = gb.readBits(5);
        let general_profile_compatibility_flags_1 = gb.readByte();
        let general_profile_compatibility_flags_2 = gb.readByte();
        let general_profile_compatibility_flags_3 = gb.readByte();
        let general_profile_compatibility_flags_4 = gb.readByte();
        let general_constraint_indicator_flags_1 = gb.readByte();
        let general_constraint_indicator_flags_2 = gb.readByte();
        let general_constraint_indicator_flags_3 = gb.readByte();
        let general_constraint_indicator_flags_4 = gb.readByte();
        let general_constraint_indicator_flags_5 = gb.readByte();
        let general_constraint_indicator_flags_6 = gb.readByte();
        let general_level_idc = gb.readByte();
        let sub_layer_profile_present_flag = [];
        let sub_layer_level_present_flag = [];
        for (let i = 0; i < max_sub_layers_minus1; i++) {
            sub_layer_profile_present_flag.push(gb.readBool());
            sub_layer_level_present_flag.push(gb.readBool());
        }
        if (max_sub_layers_minus1 > 0) {
            for (let i = max_sub_layers_minus1; i < 8; i++) { gb.readBits(2); }
        }
        for (let i = 0; i < max_sub_layers_minus1; i++) {
            if (sub_layer_profile_present_flag[i]) {
                gb.readByte(); // sub_layer_profile_space, sub_layer_tier_flag, sub_layer_profile_idc
                gb.readByte(); gb.readByte(); gb.readByte(); gb.readByte(); // sub_layer_profile_compatibility_flag
                gb.readByte(); gb.readByte(); gb.readByte(); gb.readByte(); gb.readByte(); gb.readByte();
            }
            if (sub_layer_level_present_flag[i]) {
                gb.readByte();
            }
        }
        // profile_tier_level end

        let seq_parameter_set_id = sps['seq_parameter_set_id'] = gb.readUEG();
        let chroma_format_idc = gb.readUEG();
        if (chroma_format_idc === 3) {
            sps['separate_colour_plane_flag'] = gb.readBits(1);  // separate_colour_plane_flag
        }
        let pic_width_in_luma_samples = gb.readUEG();
        let pic_height_in_luma_samples = gb.readUEG();
        let conformance_window_flag = gb.readBool();
        if (conformance_window_flag) {
            left_offset += gb.readUEG(); // conf_win_left_offset
            right_offset += gb.readUEG(); // conf_win_right_offset
            top_offset += gb.readUEG(); // conf_win_top_offset
            bottom_offset += gb.readUEG(); // conf_win_bottom_offset
        }
        let bit_depth_luma_minus8 = gb.readUEG();
        let bit_depth_chroma_minus8 = gb.readUEG();
        let log2_max_pic_order_cnt_lsb_minus4 = sps['log2_max_pic_order_cnt_lsb_minus4'] = gb.readUEG();
        let sub_layer_ordering_info_present_flag = gb.readBool();
        for (let i = sub_layer_ordering_info_present_flag ? 0 : max_sub_layers_minus1; i <= max_sub_layers_minus1; i++) {
            gb.readUEG(); // max_dec_pic_buffering_minus1[i]
            gb.readUEG(); // max_num_reorder_pics[i]
            gb.readUEG(); // max_latency_increase_plus1[i]
        }
        let log2_min_luma_coding_block_size_minus3 = gb.readUEG();
        let log2_diff_max_min_luma_coding_block_size = gb.readUEG();
        let log2_min_transform_block_size_minus2 = gb.readUEG();
        let log2_diff_max_min_transform_block_size = gb.readUEG();
        let max_transform_hierarchy_depth_inter = gb.readUEG();
        let max_transform_hierarchy_depth_intra = gb.readUEG();
        let scaling_list_enabled_flag = gb.readBool();
        if (scaling_list_enabled_flag) {
            let sps_scaling_list_data_present_flag = gb.readBool();
            if (sps_scaling_list_data_present_flag) {
                for (let sizeId = 0; sizeId < 4; sizeId++) {
                    for (let matrixId = 0; matrixId < ((sizeId === 3) ? 2 : 6); matrixId++) {
                        let scaling_list_pred_mode_flag = gb.readBool();
                        if (!scaling_list_pred_mode_flag) {
                            gb.readUEG(); // scaling_list_pred_matrix_id_delta
                        } else {
                            let coefNum = Math.min(64, (1 << (4 + (sizeId << 1))));
                            if (sizeId > 1) { gb.readSEG(); }
                            for (let i = 0; i < coefNum; i++) { gb.readSEG(); }
                        }
                    }
                }
            }
        }
        let amp_enabled_flag = gb.readBool();
        let sample_adaptive_offset_enabled_flag = gb.readBool();
        let pcm_enabled_flag = gb.readBool();
        if (pcm_enabled_flag) {
            gb.readByte(); // pcm_sample_bit_depth_luma_minus1 + pcm_sample_bit_depth_chroma_minus1
            gb.readUEG(); // log2_min_pcm_luma_coding_block_size_minus3
            gb.readUEG(); // log2_diff_max_min_pcm_luma_coding_block_size
            gb.readBool(); // pcm_loop_filter_disabled_flag
        }
        let num_short_term_ref_pic_sets = gb.readUEG();
        let num_delta_pocs = 0;
        for (let i = 0; i < num_short_term_ref_pic_sets; i++) {
            let inter_ref_pic_set_prediction_flag = false;
            if (i !== 0) { inter_ref_pic_set_prediction_flag = gb.readBool(); }
            if (inter_ref_pic_set_prediction_flag) {
                if (i === num_short_term_ref_pic_sets) { gb.readUEG(); }
                gb.readBool();
                gb.readUEG();
                let next_num_delta_pocs = 0;
                for (let j = 0; j <= num_delta_pocs; j++) {
                    let used_by_curr_pic_flag = gb.readBool();
                    let use_delta_flag = false;
                    if (!used_by_curr_pic_flag) {
                        use_delta_flag = gb.readBool();
                    }
                    if (used_by_curr_pic_flag || use_delta_flag) {
                        next_num_delta_pocs++;
                    }
                }
                num_delta_pocs = next_num_delta_pocs;
            } else {
                let num_negative_pics = gb.readUEG();
                let num_positive_pics = gb.readUEG();
                num_delta_pocs = num_negative_pics + num_positive_pics;
                for (let j = 0; j < num_negative_pics; j++) {
                    gb.readUEG();
                    gb.readBool();
                }
                for (let j = 0; j < num_positive_pics; j++) {
                    gb.readUEG();
                    gb.readBool();
                }
            }
        }
        let long_term_ref_pics_present_flag = gb.readBool();
        if (long_term_ref_pics_present_flag) {
            let num_long_term_ref_pics_sps = gb.readUEG();
            for (let i = 0; i < num_long_term_ref_pics_sps; i++) {
                for (let j = 0; j < (log2_max_pic_order_cnt_lsb_minus4 + 4); j++) { gb.readBits(1); }
                gb.readBits(1);
            }
        }
        //*
        let default_display_window_flag = false; // for calc offset
        let min_spatial_segmentation_idc = 0; // for hvcC
        let sar_width = 1, sar_height = 1;
        let fps_fixed = false, fps_den = 1, fps_num = 1;
        //*/
        let sps_temporal_mvp_enabled_flag = gb.readBool();
        let strong_intra_smoothing_enabled_flag = gb.readBool();
        let vui_parameters_present_flag = gb.readBool();
        if (vui_parameters_present_flag) {
            let aspect_ratio_info_present_flag = gb.readBool();
            if (aspect_ratio_info_present_flag) {
                let aspect_ratio_idc = gb.readByte();

                let sar_w_table = [1, 12, 10, 16, 40, 24, 20, 32, 80, 18, 15, 64, 160, 4, 3, 2];
                let sar_h_table = [1, 11, 11, 11, 33, 11, 11, 11, 33, 11, 11, 33,  99, 3, 2, 1];

                if (aspect_ratio_idc > 0 && aspect_ratio_idc <= 16) {
                    sar_width = sar_w_table[aspect_ratio_idc - 1];
                    sar_height = sar_h_table[aspect_ratio_idc - 1];
                } else if (aspect_ratio_idc === 255) {
                    sar_width = gb.readBits(16);
                    sar_height = gb.readBits(16);
                }
            }
            let overscan_info_present_flag = gb.readBool();
            if (overscan_info_present_flag) {
                gb.readBool();
            }
            let video_signal_type_present_flag = gb.readBool();
            if (video_signal_type_present_flag) {
                gb.readBits(3);
                gb.readBool();
                let colour_description_present_flag = gb.readBool();
                if (colour_description_present_flag) {
                    gb.readByte();
                    gb.readByte();
                    gb.readByte();
                }
            }
            let chroma_loc_info_present_flag = gb.readBool();
            if (chroma_loc_info_present_flag) {
                gb.readUEG();
                gb.readUEG();
            }
            let neutral_chroma_indication_flag = gb.readBool();
            let field_seq_flag = gb.readBool();
            let frame_field_info_present_flag = gb.readBool();
            default_display_window_flag = gb.readBool();
            if (default_display_window_flag) {
                gb.readUEG();
                gb.readUEG();
                gb.readUEG();
                gb.readUEG();
            }
            let vui_timing_info_present_flag = gb.readBool();
            if (vui_timing_info_present_flag) {
                fps_den = gb.readBits(32);
                fps_num = gb.readBits(32);
                let vui_poc_proportional_to_timing_flag = gb.readBool();
                if (vui_poc_proportional_to_timing_flag) {
                    gb.readUEG();
                }
                let vui_hrd_parameters_present_flag = gb.readBool();
                if (vui_hrd_parameters_present_flag) {
                    let commonInfPresentFlag = 1;
                    let nal_hrd_parameters_present_flag = false;
                    let vcl_hrd_parameters_present_flag = false;
                    let sub_pic_hrd_params_present_flag = false;
                    if (commonInfPresentFlag) {
                        nal_hrd_parameters_present_flag = gb.readBool();
                        vcl_hrd_parameters_present_flag = gb.readBool();
                        if (nal_hrd_parameters_present_flag || vcl_hrd_parameters_present_flag) {
                            sub_pic_hrd_params_present_flag = gb.readBool();
                            if (sub_pic_hrd_params_present_flag) {
                                gb.readByte();
                                gb.readBits(5);
                                gb.readBool();
                                gb.readBits(5);
                            }
                            let bit_rate_scale = gb.readBits(4);
                            let cpb_size_scale = gb.readBits(4);
                            if (sub_pic_hrd_params_present_flag) {
                                gb.readBits(4);
                            }
                            gb.readBits(5);
                            gb.readBits(5);
                            gb.readBits(5);
                        }
                    }
                    for (let i = 0; i <= max_sub_layers_minus1; i++) {
                        let fixed_pic_rate_general_flag = gb.readBool();
                        fps_fixed = fixed_pic_rate_general_flag;
                        let fixed_pic_rate_within_cvs_flag = true;
                        let cpbCnt = 1;
                        if (!fixed_pic_rate_general_flag) {
                            fixed_pic_rate_within_cvs_flag = gb.readBool();
                        }
                        let low_delay_hrd_flag = false;
                        if (fixed_pic_rate_within_cvs_flag) {
                            gb.readUEG();
                        } else {
                            low_delay_hrd_flag = gb.readBool();
                        }
                        if (!low_delay_hrd_flag) {
                            cpbCnt = gb.readUEG() + 1;
                        }
                        if (nal_hrd_parameters_present_flag) {
                            for (let j = 0; j < cpbCnt; j++) {
                                gb.readUEG(); gb.readUEG();
                                if (sub_pic_hrd_params_present_flag) {
                                    gb.readUEG(); gb.readUEG();
                                }
                            }
                            gb.readBool();
                        }
                        if (vcl_hrd_parameters_present_flag) {
                            for (let j = 0; j < cpbCnt; j++) {
                                gb.readUEG(); gb.readUEG();
                                if (sub_pic_hrd_params_present_flag) {
                                    gb.readUEG(); gb.readUEG();
                                }
                            }
                            gb.readBool();
                        }
                    }
                }
            }
            let bitstream_restriction_flag = gb.readBool();
            if (bitstream_restriction_flag) {
                let tiles_fixed_structure_flag = gb.readBool();
                let motion_vectors_over_pic_boundaries_flag = gb.readBool();
                let restricted_ref_pic_lists_flag = gb.readBool();
                min_spatial_segmentation_idc = gb.readUEG();
                let max_bytes_per_pic_denom = gb.readUEG();
                let max_bits_per_min_cu_denom = gb.readUEG();
                let log2_max_mv_length_horizontal = gb.readUEG();
                let log2_max_mv_length_vertical = gb.readUEG();
            }
        }
        let sps_extension_flag = gb.readBool(); // ignore...

        // for meta data
        let codec_mimetype = `hvc1.${general_profile_idc}.1.L${general_level_idc}.B0`;

        let sub_wc = (chroma_format_idc === 1 || chroma_format_idc === 2) ? 2 : 1;
        let sub_hc = (chroma_format_idc === 1) ? 2 : 1;
        let codec_width = pic_width_in_luma_samples - (left_offset + right_offset) * sub_wc;
        let codec_height = pic_height_in_luma_samples - (top_offset + bottom_offset) * sub_hc;
        let sar_scale = 1;
        if (sar_width !== 1 && sar_height !== 1) {
            sar_scale = sar_width / sar_height;
        }

        // variables
        sps['MinCbLog2SizeY'] = log2_min_luma_coding_block_size_minus3 + 3;
        sps['CtbLog2SizeY'] = sps['MinCbLog2SizeY'] + log2_diff_max_min_luma_coding_block_size;
        sps['MinCbSizeY'] = 1 << sps['MinCbLog2SizeY'];
        sps['CtbSizeY'] = 1 << sps['CtbLog2SizeY'];
        sps['PicWidthInMinCbsY'] = pic_width_in_luma_samples / sps['MinCbSizeY'];
        sps['PicWidthInCtbsY'] = Math.ceil(pic_width_in_luma_samples / sps['CtbSizeY']);
        sps['PicHeightInMinCbsY'] = pic_height_in_luma_samples / sps['MinCbSizeY'];
        sps['PicHeightInCtbsY'] = Math.ceil(pic_height_in_luma_samples / sps['CtbSizeY']);
        sps['PicSizeInMinCbsY'] = sps['PicWidthInMinCbsY'] * sps['PicHeightInMinCbsY'];
        sps['PicSizeInCtbsY'] = sps['PicWidthInCtbsY'] * sps['PicHeightInCtbsY'];
        sps['PicSizeInSamplesY'] = pic_width_in_luma_samples * pic_height_in_luma_samples;
        sps['PicWidthInSamplesC'] = pic_width_in_luma_samples / sub_wc;
        sps['PicHeightInSamplesC'] = pic_height_in_luma_samples / sub_hc;

        gb.destroy();
        gb = null;

        return Object.assign(sps, {
            codec_mimetype,
            profile_string: H265NaluParser.getProfileString(general_profile_idc),
            level_string: H265NaluParser.getLevelString(general_level_idc),
            profile_idc: general_profile_idc,
            bit_depth: bit_depth_luma_minus8 + 8,
            ref_frames: 1, // FIXME!!!
            chroma_format: chroma_format_idc,
            chroma_format_string: H265NaluParser.getChromaFormatString(chroma_format_idc),

            general_level_idc,
            general_profile_space,
            general_tier_flag,
            general_profile_idc,
            general_profile_compatibility_flags_1,
            general_profile_compatibility_flags_2,
            general_profile_compatibility_flags_3,
            general_profile_compatibility_flags_4,
            general_constraint_indicator_flags_1,
            general_constraint_indicator_flags_2,
            general_constraint_indicator_flags_3,
            general_constraint_indicator_flags_4,
            general_constraint_indicator_flags_5,
            general_constraint_indicator_flags_6,
            min_spatial_segmentation_idc,
            constant_frame_rate: 0 /* FIXME!! fps_fixed ? 1 : 0? */,
            chroma_format_idc,
            bit_depth_luma_minus8,
            bit_depth_chroma_minus8,
            log2_max_pic_order_cnt_lsb_minus4,

            frame_rate: {
                fixed: fps_fixed,
                fps: fps_num / fps_den,
                fps_den: fps_den,
                fps_num: fps_num,
            },

            sar_ratio: {
                width: sar_width,
                height: sar_height
            },

            codec_size: {
                width: codec_width,
                height: codec_height
            },

            present_size: {
                width: codec_width * sar_scale,
                height: codec_height
            }
        });
    }

    static parsePPS(uint8array) {
        let rbsp = H265NaluParser._ebsp2rbsp(uint8array);
        let gb = new ExpGolomb(rbsp);
        const pps = {};

        /* remove NALu Header */
        gb.readByte();
        gb.readByte();

        let pic_parameter_set_id = pps['pic_parameter_set_id'] = gb.readUEG();
        let seq_parameter_set_id = pps['seq_parameter_set_id'] = gb.readUEG();
        let dependent_slice_segments_enabled_flag = pps['dependent_slice_segments_enabled_flag'] = gb.readBool();
        let output_flag_present_flag = pps['output_flag_present_flag'] = gb.readBool();
        let num_extra_slice_header_bits = pps['num_extra_slice_header_bits'] = gb.readBits(3);
        let sign_data_hiding_enabled_flag = pps['sign_data_hiding_enabled_flag'] = gb.readBool();
        let cabac_init_present_flag = pps['cabac_init_present_flag'] = gb.readBool();
        let num_ref_idx_l0_default_active_minus1 = gb.readUEG();
        let num_ref_idx_l1_default_active_minus1 = gb.readUEG();
        let init_qp_minus26 = gb.readSEG();
        let constrained_intra_pred_flag = gb.readBool();
        let transform_skip_enabled_flag = gb.readBool();
        let cu_qp_delta_enabled_flag = gb.readBool();
        if (cu_qp_delta_enabled_flag) {
            let diff_cu_qp_delta_depth = gb.readUEG();
        }
        let cb_qp_offset = gb.readSEG();
        let cr_qp_offset = gb.readSEG();
        let pps_slice_chroma_qp_offsets_present_flag = gb.readBool();
        let weighted_pred_flag = gb.readBool();
        let weighted_bipred_flag = gb.readBool();
        let transquant_bypass_enabled_flag = gb.readBool();
        let tiles_enabled_flag = gb.readBool();
        let entropy_coding_sync_enabled_flag = gb.readBool();
        // and more ...

        // needs hvcC
        let parallelismType = 1; // slice-based parallel decoding
        if (entropy_coding_sync_enabled_flag && tiles_enabled_flag) {
            parallelismType = 0; // mixed-type parallel decoding
        } else if (entropy_coding_sync_enabled_flag) {
            parallelismType = 3; // wavefront-based parallel decoding
        } else if (tiles_enabled_flag) {
            parallelismType = 2; // tile-based parallel decoding
        }

        gb.destroy();
        gb = null;

        return Object.assign(pps, {
            parallelismType
        });
    }

    static parseSliceHeader(uint8array, nal_unit_type, spsMap, ppsMap) {
        let rbsp = H265NaluParser._ebsp2rbsp(uint8array);
        let gb = new ExpGolomb(rbsp);
        const sliceHeader = {
            pic_order_cnt_lsb: 0,
        };

        /* remove NALu Header */
        gb.readByte();
        gb.readByte();

        let first_slice_segment_in_pic_flag = gb.readBool();
        if (nal_unit_type >= 16 && nal_unit_type <= 23) {
            sliceHeader['no_output_of_prior_pics_flag'] = gb.readBool();
        }
        let pic_parameter_set_id = gb.readUEG();

        const pps = ppsMap.get(pic_parameter_set_id);
        if (!pps) {
            Log.w('H265NaluParser', `can not find PPS with slice's pic_parameter_set_id ${pic_parameter_set_id}`);

            gb.destroy();
            gb = null;
            return null;
        }

        const sps = spsMap.get(pps.seq_parameter_set_id);
        if (!sps) {
            Log.w('H265NaluParser', `can not find SPS with slice's seq_parameter_set_id ${pps.seq_parameter_set_id}`);

            gb.destroy();
            gb = null;
            return null;
        }

        sliceHeader['MaxPicOrderCntLsb'] = Math.pow(2, sps.log2_max_pic_order_cnt_lsb_minus4 + 4);

        if (!first_slice_segment_in_pic_flag) {
            if (pps.dependent_slice_segments_enabled_flag) {
                sliceHeader['dependent_slice_segment_flag'] = gb.readBool();
            }

            sliceHeader['slice_segment_address'] = gb.readBits(Math.ceil(Math.log2(sps.PicSizeInCtbsY)));
        }
        if (!sliceHeader['dependent_slice_segment_flag']) {
            for (let i = 0; i < pps.num_extra_slice_header_bits; i++) {
                gb.readBits(1); // slice_reserved_flag[i]
            }
            sliceHeader['slice_type'] = gb.readUEG();
            if (pps.output_flag_present_flag) {
                sliceHeader['pic_output_flag'] = gb.readBool();
            }
            if (sps.separate_colour_plane_flag === 1) {
                sliceHeader['colour_plane_id'] = gb.readBits(2);
            }
            if (nal_unit_type !== 19 && nal_unit_type !== 20) {
                sliceHeader['pic_order_cnt_lsb'] = gb.readBits(sps.log2_max_pic_order_cnt_lsb_minus4 + 4);
                // more...
            }
        }

        gb.destroy();
        gb = null;

        return sliceHeader;
    }

    static getChromaFormatString(chroma_idc) {
        switch (chroma_idc) {
            case 0: return '4:0:0';
            case 1: return '4:2:0';
            case 2: return '4:2:2';
            case 3: return '4:4:4';
            default: return 'Unknown';
        }
    }

    static getProfileString(profile_idc) {
        switch (profile_idc) {
            case 1: return 'Main';
            case 2: return 'Main10';
            case 3: return 'MainSP';
            case 4: return 'Rext';
            case 9: return 'SCC';
            default: return 'Unknown';
        }
    }

    static getLevelString(level_idc) {
        return (level_idc / 30).toFixed(1);
    }

    static getNalUnitTypeName(nal_unit_type) {
        switch (nal_unit_type) {
            case 0: return 'TRAIL_N';
            case 1: return 'TRAIL_R';
            case 2: return 'TSA_N';
            case 3: return 'TSA_R';
            case 4: return 'STSA_N';
            case 5: return 'STSA_R';
            case 6: return 'RADL_N';
            case 7: return 'RADL_R';
            case 8: return 'RASL_N';
            case 9: return 'RASL_R';
            case 10: return 'RSV_VCL_N10';
            case 12: return 'RSV_VCL_N12';
            case 14: return 'RSV_VCL_N14';
            case 11: return 'RSV_VCL_R11';
            case 13: return 'RSV_VCL_R13';
            case 15: return 'RSV_VCL_R15';
            case 16: return 'BLA_W_LP';
            case 17: return 'BLA_W_RADL';
            case 18: return 'BLA_N_LP';
            case 19: return 'IDR_W_RADL';
            case 20: return 'IDR_N_LP';
            case 21: return 'CRA_NUT';
            case 22: return 'RSV_IRAP_VCL22';
            case 23: return 'RSV_IRAP_VCL23';
            case 32: return 'VPS_NUT';
            case 33: return 'SPS_NUT';
            case 34: return 'PPS_NUT';
            case 35: return 'AUD_NUT';
            case 36: return 'EOS_NUT';
            case 37: return 'EOB_NUT';
            case 38: return 'FD_NUT';
            case 39: return 'PREFIX_SEI_NUT';
            case 40: return 'SUFFIX_SEI_NUT';
            default: {
                if (nal_unit_type >= 24 && nal_unit_type <= 31) {
                    return 'RSV_VCL' + nal_unit_type;
                } else if (nal_unit_type >= 41 && nal_unit_type <= 47) {
                    return 'RSV_NVCL' + nal_unit_type;
                } else if (nal_unit_type >= 48 && nal_unit_type <= 63) {
                    return 'UNSPEC' + nal_unit_type;
                }
                return 'Unspecified';
            }
        }
    }
}

export default H265NaluParser;
